import type { AncestryNode } from '../api/types'

/**
 * What spawned this.
 *
 * A tree rather than a list because the shape is the information: one shell with one
 * child is an administrator working, one shell with forty is something else, and both
 * read identically as rows.
 *
 * Drawn as a nested list, not an ARIA tree widget. A tree role promises arrow-key
 * navigation and expand/collapse, and this is static output — claiming the role
 * without the behaviour is worse for a screen-reader user than not claiming it.
 */
export function ProcessTree({
  nodes,
  applicable,
  complete,
  unobservedParents,
  depthLimited,
  rowLimited,
}: {
  nodes: AncestryNode[]
  applicable: boolean
  complete: boolean
  unobservedParents: number[]
  depthLimited: boolean
  rowLimited: boolean
}) {
  if (!applicable) {
    return (
      <p className="muted">
        This alert is not about a process — nothing it was built from named one. Most
        authentication and network alerts are like this.
      </p>
    )
  }
  if (nodes.length === 0) {
    return (
      <p className="empty">
        No process events for this host survive in the window. The tree cannot be drawn
        from what is stored.
      </p>
    )
  }

  return (
    <>
      {/* The gaps first, above the drawing. A reader who takes the tree at face value
          and finds the caveat underneath has already drawn their conclusion. */}
      {!complete && (
        <p className="muted" role="note">
          <strong>Partial.</strong>{' '}
          {unobservedParents.length > 0 &&
            `Parent pid ${unobservedParents.join(', ')} was never observed on this host, so
             what is above it is unknown — not absent. `}
          {depthLimited && 'The walk stopped at its generation limit. '}
          {rowLimited && 'This host launched more processes than one page holds. '}
        </p>
      )}
      {renderLevel(nodes, 0, 0).element}
    </>
  )
}

/**
 * Rebuild the nesting from the flat, depth-tagged list the API returns.
 *
 * The API sends pre-order with a depth on each row so the client never has to sort a
 * tree it did not build; this walks that sequence back into markup.
 */
function renderLevel(
  nodes: AncestryNode[],
  index: number,
  depth: number,
): { element: JSX.Element; next: number } {
  const items: JSX.Element[] = []
  let cursor = index

  while (cursor < nodes.length && nodes[cursor]!.depth === depth) {
    const node = nodes[cursor]!
    const child = renderLevel(nodes, cursor + 1, depth + 1)
    items.push(
      <li key={`${node.pid}-${node.event_id}`} style={{ padding: 'var(--space-1) 0' }}>
        <span className="mono">
          {/* Named as well as marked: an analyst who cannot distinguish the emphasis
              still needs to know which of these the alert was actually about. */}
          {node.focus && (
            <span className="badge sev-4" style={{ marginRight: 'var(--space-2) ' }}>
              this alert
            </span>
          )}
          <strong>{node.name || '(unnamed)'}</strong> <span className="muted">pid {node.pid}</span>
          {node.user && <span className="muted"> · {node.user}</span>}
          {node.time && (
            <span className="muted"> · {new Date(node.time).toLocaleTimeString()}</span>
          )}
        </span>
        {node.cmd_line && (
          <div className="mono muted" style={{ fontSize: 'var(--text-xs)', wordBreak: 'break-all' }}>
            {node.cmd_line}
          </div>
        )}
        {child.element}
      </li>,
    )
    cursor = child.next
  }

  return {
    element:
      items.length === 0 ? (
        <></>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            paddingLeft: depth === 0 ? 0 : 'var(--space-4)',
            margin: 0,
            borderLeft: depth === 0 ? 'none' : '1px solid var(--border)',
          }}
        >
          {items}
        </ul>
      ),
    next: cursor,
  }
}
